// SAB-466: Locance geo-compliance FRONT-END gather — the state-machine hook.
//
// Drives one geo-check "event": load the in-page Locance scripts, generate a
// fresh sessionId, run the HTML5 Location collector + Device-Profiling toolkit,
// wait for the browser location-consent callback, and — ONLY once the collector
// reports success (statusCode === 0 && locationData.code === "0") — call the
// locance-compliance-check edge function for the authoritative verdict.
//
// Server is authoritative (mirrors Persona, NFLFundsSection.tsx recheckKyc...):
// the collector callback is a *consent/coverage* gate only. It proves the
// browser handed over a location; it is NOT the compliance decision. The
// verdict comes exclusively from the backend, which re-derives it from the
// sessionId server-side. A caller must gate money on `verdict.result`, never
// on the collector callback alone.
//
// SAB-493: the hook now SURFACES structured block reasons for the 4c geo-block
// screen (consent-denied / transient / out-of-region / VPN-proxy / device) and
// retries the collector once on a transient error (HTML5 guide p6). The reason
// parsing is DISPLAY-ONLY — see the guardrail on `geoBlock` below. It does NOT
// change what releases money: run() still resolves the raw verdict and callers
// fail-closed on anything but result === 'Compliant'.

import { useCallback, useEffect, useRef, useState } from 'react';
import { Capacitor } from '@capacitor/core';
import { supabase } from '@/integrations/supabase/client';
import {
  loadLocanceScripts,
  loadLocanceProfilingOnly,
  startProfiling,
  type LocanceHtml5Result,
} from '@/lib/locance';
// SAB-644: native App-based location submission (Locance appData interface).
import { submitAppDataLocation } from '@/lib/locanceAppData';
// SAB-644 TEMP: native geo-gather diagnostic beacon. Remove with the rest of the instrumentation.
import { sendGeoGatherBeacon } from '@/lib/geoGatherBeacon';
import { resetGeoShimLog } from '@/lib/nativeGeolocationShim';

/* ── Public types ── */

export type LocanceGeoState =
  | 'idle' // nothing run yet
  | 'prompting' // scripts loading + browser consent + collector running
  | 'granted' // collector returned statusCode:0 && locationData.code:"0" (location captured)
  | 'checking' // calling locance-compliance-check
  | 'verified' // backend returned a verdict — see `verdict.result`
  | 'denied' // location consent denied / collector reported non-zero status
  | 'error'; // script load / watchdog timeout / network / config error

/**
 * Structured, DISPLAY-ONLY signals parsed out of the raw Locance ComplianceResp
 * so the 4c geo-block screen can explain *why* a Noncompliant verdict blocked.
 *
 * GUARDRAIL: nothing here gates money. The only thing that releases a deposit/
 * withdrawal is `verdict.result === 'Compliant'` (enforced by the caller on the
 * value run() resolves). These fields drive copy/iconography only.
 */
export interface ComplianceSignals {
  /** zoneCheck result — 'In' when physically inside an allowed gaming zone. */
  zoneResult: string | null;
  /** Physically-detected state (prefers HTML5 GPS civicAddress, else IP). */
  detectedState: string | null;
  /** IP the browser submitted to the HTML5 collector. */
  submitterIp: string | null;
  /** IP the compliance server observed the request from. */
  detectedIp: string | null;
  /**
   * VPN/proxy — derived from STRUCTURED fields ONLY: ipInfo.proxy /
   * anonymizerStatus / submitterIp≠detectedIp. NEVER from the
   * "IP Location Unreliable Due to Routing or Connection Type!" reason STRING
   * (that string appears on legit Compliant mobile/carrier-NAT verdicts —
   * treating it as a block would false-block cellular users).
   */
  proxyDetected: boolean;
  /** Locance complianceReason strings, for debug/secondary copy. */
  complianceReason: string[];
}

/** Normalized verdict returned by the locance-compliance-check edge fn. */
export interface LocanceVerdict {
  result: 'Compliant' | 'Noncompliant';
  upstream: 'Compliant' | 'Noncompliant' | 'Review' | 'Unknown';
  reason: string;
  /**
   * SAB-570: the FRESH Locance html5 session id this verdict was gathered for.
   * Attach it to the money request body as `geo_session_id` so the SERVER
   * re-verifies + claims the same session (one-per-attempt replay guard).
   */
  sessionId: string;
  /** Locance's own reasons for the verdict (surfaced from the backend). */
  complianceReason: string[];
  /** Parsed structured signals (display-only — see ComplianceSignals). */
  signals: ComplianceSignals;
  raw: unknown; // full Locance response for audit/debug
}

/**
 * Why a geo-check blocked, for the 4c GeoBlockScreen (SAB-490). Some kinds are
 * produced by this hook (the Locance layer); `profile_incomplete` /
 * eligibility-`out_of_region` are produced by the money-moment components from
 * check-eligibility and reuse this same shape/screen.
 */
export type GeoBlockKind =
  | 'consent_denied' // collector: browser location permission refused (code 1 / consentState denied)
  | 'transient' // collector: recoverable (code 2/3, transient statusCode, watchdog timeout) — after one retry
  | 'unavailable' // collector: other non-success (input error, scripts/services unavailable)
  | 'out_of_region' // verdict: physically outside an allowed gaming zone (or eligibility state ineligible)
  | 'vpn_proxy' // verdict: proxy/anonymizer/submitterIp mismatch (STRUCTURED signals only)
  | 'device' // verdict: Noncompliant for device-profile / other reasons
  | 'profile_incomplete'; // profile layer: state/age not verified (set by money moments, not this hook)

export interface GeoBlockReason {
  kind: GeoBlockKind;
  /** State name shown in out_of_region copy — see stateSource for its meaning. */
  detectedState?: string | null;
  /**
   * Where `detectedState` comes from: 'detected' = physically-detected by Locance
   * (this hook / the Locance layer); 'declared' = the user's registered state
   * (the profile-eligibility layer via check-eligibility). Drives the chip label
   * ("Detected location" vs "Registered state") and whether a "Refresh location"
   * action makes sense. Defaults to 'detected'.
   */
  stateSource?: 'detected' | 'declared';
  /** Locance complianceReason strings — secondary/debug copy. */
  complianceReason?: string[];
  /** Collector diagnostics — debug copy. */
  locationCode?: string;
  statusCode?: number;
  consentState?: string;
}

export interface UseLocanceGeoCheck {
  state: LocanceGeoState;
  verdict: LocanceVerdict | null;
  /** Human-readable error/denial detail for UX; null unless state is error/denied. */
  detail: string | null;
  /**
   * Structured block reason for the 4c screen, or null when not blocked.
   * DISPLAY-ONLY — never gate money on this; gate on the run() verdict.
   */
  geoBlock: GeoBlockReason | null;
  /**
   * Kick off a geo-check event. Safe to call again — supersedes any in-flight
   * run. Resolves with the authoritative verdict once the backend responds, or
   * null if the location was denied / the check errored / timed out / was
   * superseded. Callers MUST fail closed: proceed only on
   * `(await run())?.result === 'Compliant'`.
   */
  run: () => Promise<LocanceVerdict | null>;
  /** Cancel any in-flight run + scheduled re-check and return to idle. */
  reset: () => void;
}

/**
 * TODO(geo-FAQ follow-up): replace with the real Locance geo-eligibility FAQ
 * page + William's Portal URL. Placeholder so the 4c GeoBlockScreen (Phase 2)
 * can link consent-denied / VPN-proxy cases somewhere. A follow-up ticket
 * tracks building the FAQ page and swapping this in.
 */
export const GEO_FAQ_URL = '/help/location-eligibility';

/* ── Config ── */

// The collector's own timeout is 30s (arg 4 below). Our watchdog is a little
// longer so a well-behaved late callback still wins over a false timeout.
const COLLECTOR_WATCHDOG_MS = 35_000;

// Re-run this far ahead of complianceExpiration so a fresh verdict is always
// in hand before the old one lapses (Locance guidance: ~10s before expiry).
const REFRESH_LEAD_MS = 10_000;

// Collector top-level statusCode values that are RECOVERABLE (HTML5 guide value
// set): 13=WatchTimeout, 14=ConnectionTimeout, 17=NetworkError, 19=SystemError.
// 48=input-error is NOT transient (config/request bug) → 'unavailable'.
const TRANSIENT_STATUS = new Set<number>([13, 14, 17, 19]);

/* ── Helpers ── */

/**
 * sessionId: >=10 chars, no PII, unique per event. 2-char method marker + 32 hex
 * (34 chars), within the backend's ^[A-Za-z0-9_-]{1,128}$ bound.
 *
 * SAB-644: the 2-char prefix stamps the COLLECTION METHOD so it rides through the
 * single geo_session_id value the server already receives — no parallel geo_source
 * field threaded into every money endpoint + call site. The server derives the
 * Locance request parameter from it (see geo-gate.ts sessionSource):
 *   'sf' → HTML5 collector (web)   → html5SessionId
 *   'sa' → appData interface (app) → appSessionId
 */
function newSessionId(native: boolean): string {
  const c = globalThis.crypto;
  const rand =
    c && 'randomUUID' in c
      ? c.randomUUID().replace(/-/g, '')
      : Array.from(c.getRandomValues(new Uint8Array(16)))
          .map((b) => b.toString(16).padStart(2, '0'))
          .join('');
  return `${native ? 'sa' : 'sf'}${rand}`;
}

/**
 * The consent domain Locance uses to scope/key the stored html5 location
 * session (§5.1.2: the FQDN the location was reported from — it must be a
 * domain registered to the Locance service, or compliance can't resolve the
 * session → "HTML5 Session Id does not exist").
 *
 * SAB-466 STAGING TEST override: VITE_LOCANCE_CONSENT_DOMAIN forces a
 * registered FQDN even on the *.vercel.app preview (whose raw host isn't
 * registered to the demo service). Prod/staging real domains leave it UNSET
 * and fall through to the host-based logic, which already emits
 * 'sabotagefantasy.com' on *.sabotagefantasy.com — so this override changes
 * nothing when the env var is absent.
 */
function consentDomain(): string {
  const override = import.meta.env.VITE_LOCANCE_CONSENT_DOMAIN as string | undefined;
  if (override) return override;
  const host = typeof window !== 'undefined' ? window.location.hostname : '';
  return host.endsWith('sabotagefantasy.com') ? 'sabotagefantasy.com' : host;
}

function asObj(v: unknown): Record<string, unknown> | null {
  return v && typeof v === 'object' ? (v as Record<string, unknown>) : null;
}

/**
 * Parse the raw Locance ComplianceResp into structured, DISPLAY-ONLY signals
 * (see ComplianceSignals). Defensive: every field is optional-chained and falls
 * back to null/false, so a shape drift degrades to "no signal", never a throw.
 */
function parseComplianceSignals(raw: unknown): ComplianceSignals {
  const empty: ComplianceSignals = {
    zoneResult: null,
    detectedState: null,
    submitterIp: null,
    detectedIp: null,
    proxyDetected: false,
    complianceReason: [],
  };
  const root = asObj(raw);
  if (!root) return empty;
  // Verdict may sit at root or under a ComplianceResp envelope.
  const env = asObj(root.ComplianceResp) ?? asObj(root.complianceResp) ?? root;

  // complianceReason: array or bare string.
  const cr = env.complianceReason;
  const complianceReason = Array.isArray(cr)
    ? cr.filter((x): x is string => typeof x === 'string')
    : typeof cr === 'string' && cr
      ? [cr]
      : [];

  // zoneCheck.zoneInfo[0].result ('In' | 'Out' | …)
  const zoneCheck = asObj(env.zoneCheck);
  const zoneInfo = zoneCheck && Array.isArray(zoneCheck.zoneInfo) ? zoneCheck.zoneInfo : [];
  const zone0 = zoneInfo.length ? asObj(zoneInfo[0]) : null;
  const zoneResult = zone0 && typeof zone0.result === 'string' ? zone0.result : null;

  // html5Location (physical GPS) — preferred detected-state + submitterIp source.
  const html5 = asObj(env.html5Location);
  const civic = html5 ? asObj(html5.civicAddress) : null;
  const submitterIp =
    html5 && typeof html5.submitterIpAddress === 'string' ? html5.submitterIpAddress : null;

  // ipLocation — detected IP + fallback state.
  const ipLoc = asObj(env.ipLocation);
  const ipInfo = ipLoc ? asObj(ipLoc.ipInfo) : null;
  const detectedIp = ipInfo && typeof ipInfo.ipAddress === 'string' ? ipInfo.ipAddress : null;

  // detected state: prefer physical GPS civicAddress.state, else IP stateData.
  let detectedState: string | null = null;
  if (civic && typeof civic.state === 'string') {
    detectedState = civic.state;
  } else {
    const ipLocation = ipInfo ? asObj(ipInfo.location) : null;
    const stateData = ipLocation ? asObj(ipLocation.stateData) : null;
    if (stateData) {
      if (typeof stateData.stateCode === 'string') detectedState = stateData.stateCode.toUpperCase();
      else if (typeof stateData.state === 'string') detectedState = stateData.state;
    }
  }

  // VPN/proxy — STRUCTURED signals ONLY (guardrail; never the reason string).
  const proxy = ipInfo ? asObj(ipInfo.proxy) : null;
  const proxyFlagged =
    !!proxy &&
    (proxy.proxyType != null ||
      proxy.proxyName != null ||
      proxy.proxyLevel != null ||
      proxy.proxyProvider != null);
  const anonFlagged = !!ipInfo && ipInfo.anonymizerStatus != null;
  const ipMismatch = !!submitterIp && !!detectedIp && submitterIp !== detectedIp;
  const proxyDetected = proxyFlagged || anonFlagged || ipMismatch;

  return { zoneResult, detectedState, submitterIp, detectedIp, proxyDetected, complianceReason };
}

/**
 * Classify a NONCOMPLIANT verdict into a block reason for the 4c screen.
 * Order matters: VPN/proxy (structured) → out-of-region (zone) → device/other.
 * Called ONLY for a Noncompliant verdict; never affects the money gate.
 */
function classifyVerdictBlock(signals: ComplianceSignals): GeoBlockReason {
  const base = {
    detectedState: signals.detectedState,
    complianceReason: signals.complianceReason,
  };
  if (signals.proxyDetected) return { kind: 'vpn_proxy', ...base };
  if (signals.zoneResult && signals.zoneResult.toLowerCase() !== 'in') {
    // Physically-detected out-of-region (Locance zone), not the declared state.
    return { kind: 'out_of_region', stateSource: 'detected', ...base };
  }
  return { kind: 'device', ...base };
}

/**
 * complianceExpiration may live under a few shapes in the raw response and be
 * ISO-8601 or epoch (s/ms). Returns absolute ms, or null if absent/unparseable
 * (in which case we simply don't schedule an auto-refresh).
 */
function parseExpiration(raw: unknown): number | null {
  if (!raw || typeof raw !== 'object') return null;
  const root = raw as Record<string, unknown>;
  const env =
    (root.ComplianceResp as Record<string, unknown> | undefined) ??
    (root.complianceResp as Record<string, unknown> | undefined) ??
    root;
  const v = env?.complianceExpiration ?? env?.expiresAt ?? env?.expiration;
  if (typeof v === 'string') {
    const ms = Date.parse(v);
    return Number.isFinite(ms) ? ms : null;
  }
  if (typeof v === 'number' && Number.isFinite(v)) {
    return v < 1e12 ? v * 1000 : v; // 10-digit = seconds, 13-digit = ms
  }
  return null;
}

/* ── Hook ── */

export function useLocanceGeoCheck(): UseLocanceGeoCheck {
  const [state, setState] = useState<LocanceGeoState>('idle');
  const [verdict, setVerdict] = useState<LocanceVerdict | null>(null);
  const [detail, setDetail] = useState<string | null>(null);
  const [geoBlock, setGeoBlock] = useState<GeoBlockReason | null>(null);

  // Monotonic token so stale collector callbacks / awaited backends from a
  // superseded run() are ignored (reentrancy + unmount safety).
  const runIdRef = useRef(0);
  const watchdogRef = useRef<number | null>(null);
  const refreshRef = useRef<number | null>(null);
  const mountedRef = useRef(true);
  // Resolver for the promise returned by the current in-flight run(), so a
  // superseding run() / reset() / unmount can settle a pending awaiter with
  // null instead of leaving it hanging.
  const pendingSettleRef = useRef<((v: LocanceVerdict | null) => void) | null>(null);

  const clearTimers = useCallback(() => {
    if (watchdogRef.current !== null) {
      window.clearTimeout(watchdogRef.current);
      watchdogRef.current = null;
    }
    if (refreshRef.current !== null) {
      window.clearTimeout(refreshRef.current);
      refreshRef.current = null;
    }
  }, []);

  // `run` is declared via ref so the auto-refresh timer + 'online' listener can
  // call the latest version without re-subscribing on every state change.
  const runRef = useRef<() => void>(() => {});

  // Schedule a re-run ~10s before the verdict's complianceExpiration so a live
  // verdict is always in hand. No-op if the response carries no parseable expiry.
  const scheduleRefresh = useCallback((raw: unknown) => {
    const expiresAtMs = parseExpiration(raw);
    if (expiresAtMs === null) return;
    const delay = expiresAtMs - Date.now() - REFRESH_LEAD_MS;
    if (delay <= 0) return; // already inside the lead window / expired — leave to caller
    if (refreshRef.current !== null) window.clearTimeout(refreshRef.current);
    refreshRef.current = window.setTimeout(() => {
      if (!mountedRef.current) return;
      runRef.current();
    }, delay);
  }, []);

  // Backend call — server is authoritative. Re-checks staleness against the run
  // token before touching state, so a superseded run's response is ignored.
  const verifyWithBackend = useCallback(
    async (
      sessionId: string,
      myRunId: number,
      settle: (v: LocanceVerdict | null) => void,
      profilingDone: boolean,
    ) => {
      const isStale = () => myRunId !== runIdRef.current || !mountedRef.current;
      setState('checking');
      try {
        // We use one sessionId for both collectors, so dProfileSessionId is the
        // same value — but only send it when profiling actually completed, so
        // Locance doesn't wait on a device profile that never arrived.
        //
        // SAB-644: reference the session under the parameter matching how its
        // location was collected — the same 'sa'/'sf' prefix the server reads.
        // Native (appData) → appSessionId; web (HTML5) → html5SessionId. This is
        // the client PRE-CHECK; the authoritative gate (enforceGeoGate) derives
        // the identical routing server-side.
        const isAppData = sessionId.startsWith('sa');
        const { data, error } = await supabase.functions.invoke(
          'locance-compliance-check',
          {
            body: {
              ...(isAppData ? { appSessionId: sessionId } : { html5SessionId: sessionId }),
              ...(profilingDone ? { dProfileSessionId: sessionId } : {}),
            },
          },
        );
        if (isStale()) {
          settle(null);
          return;
        }

        // invoke-level transport error, or the backend's 500 config error
        // shape ({ error: 'configuration_error', ... }). A *verdict* of
        // Noncompliant is NOT an error — it's a valid result.
        if (error) {
          setDetail(error.message ?? 'Compliance check failed.');
          setState('error');
          settle(null);
          return;
        }
        if (data && typeof data === 'object' && 'error' in data && !('result' in data)) {
          setDetail(
            String((data as { detail?: unknown }).detail ?? (data as { error?: unknown }).error),
          );
          setState('error');
          settle(null);
          return;
        }

        // Build the verdict explicitly — DON'T cast `data` straight to
        // LocanceVerdict: `signals` is client-derived, not on the wire.
        const d = data as {
          result: 'Compliant' | 'Noncompliant';
          upstream: LocanceVerdict['upstream'];
          reason: string;
          raw: unknown;
        };
        const signals = parseComplianceSignals(d.raw);
        const v: LocanceVerdict = {
          result: d.result,
          upstream: d.upstream,
          reason: d.reason,
          sessionId, // SAB-570: the fresh session this verdict was gathered for
          complianceReason: signals.complianceReason,
          signals,
          raw: d.raw,
        };
        setVerdict(v);
        // DISPLAY-ONLY: classify a Noncompliant into a 4c block reason. This
        // NEVER gates money — settle(v) below hands the raw verdict to the
        // caller, which fail-closes on anything but result === 'Compliant'.
        setGeoBlock(v.result === 'Compliant' ? null : classifyVerdictBlock(signals));
        setState('verified');
        scheduleRefresh(v.raw);
        // Resolve with the verdict — Compliant AND Noncompliant both land here.
        // The caller fail-closes on anything but result === 'Compliant'.
        settle(v);
      } catch (err) {
        if (isStale()) {
          settle(null);
          return;
        }
        setDetail((err as Error)?.message ?? 'Compliance check failed.');
        setState('error');
        settle(null);
      }
    },
    [scheduleRefresh],
  );

  const run = useCallback((): Promise<LocanceVerdict | null> => {
    resetGeoShimLog(); // SAB-644 TEMP: start a fresh geolocation call-sequence for this gather
    // Abandon any previous in-flight run's awaiter so it can't hang.
    if (pendingSettleRef.current) {
      pendingSettleRef.current(null);
      pendingSettleRef.current = null;
    }

    const myRunId = ++runIdRef.current;
    const isStale = () => myRunId !== runIdRef.current || !mountedRef.current;

    return new Promise<LocanceVerdict | null>((resolve) => {
      // Idempotent, single-shot resolver. Every terminal path calls it exactly
      // once; guards ensure a late/duplicate call is a no-op.
      let settled = false;
      const settle = (v: LocanceVerdict | null) => {
        if (settled) return;
        settled = true;
        if (pendingSettleRef.current === settle) pendingSettleRef.current = null;
        resolve(v);
      };
      pendingSettleRef.current = settle;

      // SAB-644 TEMP: the sessionId of the current collector attempt, so the watchdog /
      // catch beacons can correlate to geo_verdicts. Remove with the beacon.
      let currentSessionId: string | null = null;

      clearTimers();
      setVerdict(null);
      setDetail(null);
      setGeoBlock(null);
      setState('prompting');

      // Watchdog: if the collector never calls back, fail rather than hang.
      // Re-armed per collector attempt (the code:2/3 retry gets a fresh window).
      const armWatchdog = () => {
        if (watchdogRef.current !== null) window.clearTimeout(watchdogRef.current);
        watchdogRef.current = window.setTimeout(() => {
          sendGeoGatherBeacon({ outcome: 'watchdog_timeout', scripts_loaded: true, session_id: currentSessionId });
          if (!isStale()) {
            setDetail('Location check timed out. Check your connection and try again.');
            setGeoBlock({ kind: 'transient' });
            setState('error');
          }
          settle(null);
        }, COLLECTOR_WATCHDOG_MS);
      };

      // One HTML5 collector attempt (WEB). `attempt` 0 = first run; 1 = the single
      // retry the HTML5 guide prescribes on a transient location error. `makeSessionId`
      // is supplied by the caller so the sessionId's sa/sf marker is a CONSEQUENCE of
      // the same gate that chose this collector — never an independent literal here.
      const startAttempt = (attempt: number, makeSessionId: () => string) => {
        const sessionId = makeSessionId();
        currentSessionId = sessionId; // SAB-644 TEMP

        // Fire device profiling in PARALLEL. It's a SOFT signal: startProfiling
        // resolves true/false (never rejects, never blocks). We fold
        // dProfileSessionId into the backend call only if it completes in time.
        const profilingPromise = startProfiling(sessionId);

        // HTML5 Location collector. Args per the Locance HTML5 guide:
        // (sessionId, gps, retries, timeoutMs, callback, consentDomain, ...flags)
        const onCollected = (result: LocanceHtml5Result) => {
          if (isStale()) return settle(null);
          clearTimers();

          // Verified shape: statusCode is a top-level NUMBER, locationData.code
          // is a STRING "0" on success. Use string compare for code.
          const ok = result?.statusCode === 0 && result?.locationData?.code === '0';
          // SAB-644 TEMP: record the collector outcome (success or the exact failure codes).
          sendGeoGatherBeacon({
            outcome: ok ? 'success' : 'collector_nonsuccess',
            scripts_loaded: true,
            status_code: typeof result?.statusCode === 'number' ? result.statusCode : null,
            location_code: typeof result?.locationData?.code === 'string' ? result.locationData.code : null,
            consent_state: typeof result?.consentState === 'string' ? result.consentState : null,
            session_id: sessionId,
          });
          if (ok) {
            // Location captured (hard gate passed). Wait for the bounded (≤7s)
            // profiling result, then ask the backend for the authoritative
            // verdict. The collector success is NOT the decision; Locance is.
            setState('granted');
            void (async () => {
              const profilingDone = await profilingPromise;
              if (isStale()) return settle(null);
              await verifyWithBackend(sessionId, myRunId, settle, profilingDone);
            })();
            return;
          }

          // Non-success — classify the reason for the 4c screen.
          const code = result?.locationData?.code;
          const sc = result?.statusCode;
          const consent = result?.consentState;
          const isTransient =
            code === '2' || code === '3' || (typeof sc === 'number' && TRANSIENT_STATUS.has(sc));

          // (A) HTML5 guide p6: on a transient location error, rerun the
          // collector ONCE (it already waited out its own watchDuration/timeout
          // before this callback), then fail to the transient/FAQ screen.
          if (isTransient && attempt === 0) {
            setState('prompting');
            armWatchdog(); // fresh window for the retry
            startAttempt(1, makeSessionId);
            return;
          }

          const kind: GeoBlockKind =
            code === '1' || consent === 'denied'
              ? 'consent_denied'
              : isTransient
                ? 'transient'
                : 'unavailable';
          setDetail(
            `Location not available (statusCode ${sc ?? '?'}, code ${code ?? '?'}).`,
          );
          setGeoBlock({ kind, locationCode: code, statusCode: sc, consentState: consent });
          setState('denied');
          return settle(null);
        };

        try {
          window.lshtml5(sessionId, true, 0, 30_000, onCollected, consentDomain(), true, 30);
        } catch (err) {
          sendGeoGatherBeacon({ outcome: 'lshtml5_threw', scripts_loaded: true, session_id: sessionId, error_message: (err as Error)?.message ?? 'lshtml5 threw' }); // SAB-644 TEMP
          if (isStale()) return settle(null);
          clearTimers();
          setDetail((err as Error)?.message ?? 'Failed to start location check.');
          setGeoBlock({ kind: 'unavailable' });
          setState('error');
          return settle(null);
        }
      };

      // SAB-644: one NATIVE (appData) collector attempt. Mirrors startAttempt but gathers the
      // device location via Capacitor and POSTs it to the Locance appData interface instead of
      // calling window.lshtml5 (which cannot resolve an HTML5 session in the WebView). Device
      // profiling (lsdp) still runs in parallel, exactly as on web. `attempt` 0/1 gives the same
      // single retry on a transient miss.
      const startAttemptNative = (attempt: number, makeSessionId: () => string) => {
        const sessionId = makeSessionId();
        currentSessionId = sessionId; // SAB-644 TEMP

        const profilingPromise = startProfiling(sessionId);

        void (async () => {
          let res;
          try {
            res = await submitAppDataLocation(sessionId);
          } catch (err) {
            // Unexpected (e.g. unprovisioned VITE_LOCANCE_APP_ID throws): fail closed, loudly.
            sendGeoGatherBeacon({ outcome: 'appdata_threw', scripts_loaded: true, session_id: sessionId, geo_method: 'appData:getCurrentPosition', geo_fix: false, error_message: (err as Error)?.message ?? 'submitAppDataLocation threw' }); // SAB-644 TEMP
            if (isStale()) return settle(null);
            clearTimers();
            setDetail((err as Error)?.message ?? 'Failed to submit location.');
            setGeoBlock({ kind: 'unavailable' });
            setState('error');
            return settle(null);
          }
          if (isStale()) return settle(null);
          clearTimers();

          sendGeoGatherBeacon({
            outcome: res.ok ? 'appdata_success' : 'appdata_nonsuccess',
            scripts_loaded: true,
            status_code: res.statusCode,
            // SAB-644: carry the FULL appData response (http/statusCode/statusMsg/body)
            // so the rejection reason is readable server-side instead of a null status_code.
            error_message: res.detail ?? null,
            // SAB-644: record whether we actually obtained a position before the POST, so a
            // "No latitude specified" reads as OUR gather failing, not a Locance rejection.
            geo_method: res.geoMethod,
            geo_fix: res.positionObtained,
            geo_error: res.geoError,
            session_id: sessionId,
          });

          if (res.ok) {
            // Location submitted. Wait out the bounded profiling window, then ask the backend
            // for the authoritative verdict (referencing the session under appSessionId).
            setState('granted');
            void (async () => {
              const profilingDone = await profilingPromise;
              // SAB-644 TEMP: record whether device profiling actually COMPLETED (tmx_profiling_complete
              // fired) on native — not just whether lsdp loaded. Distinguishes "lsdp ran but timed out"
              // from "lsdp completed"; read alongside the compliance raw-probe's deviceProfile node.
              sendGeoGatherBeacon({
                outcome: 'native_profiling',
                session_id: sessionId,
                error_message: `profilingDone=${profilingDone} lsdp_present=${typeof window !== 'undefined' && !!(window as unknown as { lsdp?: unknown }).lsdp}`,
              });
              if (isStale()) return settle(null);
              await verifyWithBackend(sessionId, myRunId, settle, profilingDone);
            })();
            return;
          }

          // Non-success — single retry on a transient miss, same as the HTML5 path.
          if (res.retriable && attempt === 0) {
            setState('prompting');
            armWatchdog();
            startAttemptNative(1, makeSessionId);
            return;
          }

          setDetail(res.detail ?? 'Location not available.');
          setGeoBlock({ kind: res.kind ?? 'unavailable' });
          setState(res.kind === 'consent_denied' ? 'denied' : 'error');
          return settle(null);
        })();
      };

      armWatchdog();

      // SAB-644: the appData NATIVE path is gated to soap_test_accounts (the same
      // per-account gate as device-ping + the Soap deposit/withdraw options) until it's
      // proven. A native NON-test user, and every web user, keep today's HTML5 collector
      // path completely unchanged.
      const isNative = Capacitor.isNativePlatform();

      void (async () => {
        // Decide appData vs HTML5. Only a native soap_test_account gets the appData path;
        // fail-closed to the HTML5 path (today's behavior) on any RPC error or non-membership.
        // The RPC is only ever called on native, so web makes no extra call and is untouched.
        let useAppData = false;
        if (isNative) {
          try {
            // Cast: soap_access_enabled isn't in the generated RPC types (same as
            // WalletDeposit / NFLFundsSection, which read it the same way).
            const { data, error } = await (supabase as any).rpc('soap_access_enabled');
            useAppData = !error && data === true;
          } catch {
            useAppData = false;
          }
        }
        if (isStale()) return settle(null);
        try {
          // The appData path loads ONLY the profiling toolkit (lsdp) — the HTML5 collector
          // is unused there, so its load must not be fatal. Everyone else loads both as before.
          await (useAppData ? loadLocanceProfilingOnly() : loadLocanceScripts());
        } catch (err) {
          sendGeoGatherBeacon({ outcome: 'script_load_error', scripts_loaded: false, error_message: (err as Error)?.message ?? 'loadLocanceScripts threw' }); // SAB-644 TEMP
          if (isStale()) return settle(null);
          clearTimers();
          setDetail((err as Error)?.message ?? 'Failed to load location services.');
          setGeoBlock({ kind: 'unavailable' });
          setState('error');
          return settle(null);
        }
        if (isStale()) return settle(null);
        // The HTML5 path requires the collector; the appData path requires nothing here
        // (appData is a direct POST and lsdp is a best-effort soft signal via startProfiling).
        if (!useAppData && (!window.lshtml5 || !window.lsdp)) {
          sendGeoGatherBeacon({ outcome: 'scripts_missing', scripts_loaded: true }); // SAB-644 TEMP
          clearTimers();
          setDetail('Location services unavailable.');
          setGeoBlock({ kind: 'unavailable' });
          setState('error');
          return settle(null);
        }

        // The sa/sf prefix is derived HERE from the single gate value, and handed to
        // whichever collector that same value selects — so the marker and the collection
        // method cannot diverge (SAB-644 review point 1). A bug in newSessionId can't send
        // native down the html5 param or vice versa, because there is one decision, not two.
        const makeSessionId = () => newSessionId(useAppData);
        if (useAppData) startAttemptNative(0, makeSessionId);
        else startAttempt(0, makeSessionId);
      })();
    });
  }, [clearTimers, verifyWithBackend]);

  const reset = useCallback(() => {
    runIdRef.current++; // invalidate any in-flight run
    if (pendingSettleRef.current) {
      pendingSettleRef.current(null);
      pendingSettleRef.current = null;
    }
    clearTimers();
    setVerdict(null);
    setDetail(null);
    setGeoBlock(null);
    setState('idle');
  }, [clearTimers]);

  // Keep the latest run() reachable from timers/listeners without re-binding.
  useEffect(() => {
    runRef.current = run;
  }, [run]);

  // Re-verify on network change — the closest client-observable proxy for the
  // "re-run on IP change" guidance (true IP-change detection is server-side).
  // Only meaningful once a verdict exists; a network flap mid-idle is ignored.
  useEffect(() => {
    const onOnline = () => {
      if (verdict !== null) runRef.current();
    };
    window.addEventListener('online', onOnline);
    return () => window.removeEventListener('online', onOnline);
  }, [verdict]);

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
      clearTimers();
      if (pendingSettleRef.current) {
        pendingSettleRef.current(null);
        pendingSettleRef.current = null;
      }
    };
  }, [clearTimers]);

  return { state, verdict, detail, geoBlock, run, reset };
}
